import React, { useEffect, useState } from "react";
import ClientSidebarLinks from "./ClientSidebarLinks";
import { useAuth } from "../../context/AuthContext";

type ProgressStep = {
  label: string;
  date: string;
  active: boolean;
};

type WorkItem = {
  label: string;
  value: number;
  color: string;
};

type MilestoneStatus = "completed" | "in-progress" | "pending";

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetch(`${API_BASE}/get_project_progress.php?user_id=${user.id}`)
        .then(res => res.json())
        .then(data => {
          setSteps(data.timeline || []);
          setWorkItems(data.work_breakdown || []);
          setMilestones(data.milestones || []);
          setLoading(false);
        })
        .catch(err => {
          console.error("Error fetching progress:", err);
          setLoading(false);
        });
    }
  }, [user]);

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-md p-5 border-r border-gray-100">
        <h2 className="text-2xl font-bold text-green-600 mb-6">
          {user?.name || "Client"}
        </h2>

        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Contact Team
          </p>
          <button className="w-full px-4 py-2 rounded-lg bg-green-500 text-white text-sm font-medium shadow hover:bg-green-600 transition-colors">
            Contact Team
          </button>
        </div>

        <nav className="space-y-2 text-sm">
          <ClientSidebarLinks active="progress" />
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Project Progress
            </h1>
            <p className="text-gray-500 text-sm">
              Detailed progress tracking for your current project
            </p>
          </div>
        </header>

        {loading ? (
          <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500">
            Loading progress data...
          </div>
        ) : (
          <>
            {/* Completion Timeline */}
            <section className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Completion Timeline
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Overview of key delivery milestones
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                  On Track
                </span>
              </div>

              {/* Timeline line with steps */}
              <div className="relative mt-4">
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2" />

                <div className="grid grid-cols-5 gap-4 text-sm text-gray-600">
                  {steps.map((step, index) => (
                    <div key={step.label} className="flex flex-col items-center relative z-10">
                      <div
                        className={`flex items-center justify-center h-8 w-8 rounded-full border-2 text-xs font-semibold ${step.active
                          ? "bg-green-500 border-green-500 text-white"
                          : "bg-white border-gray-300 text-gray-500"
                          }`}
                      >
                        {index + 1}
                      </div>
                      <p className="mt-3 text-xs font-medium text-gray-700 text-center">
                        {step.label}
                      </p>
                      <p className="text-[11px] text-gray-400 text-center">
                        {step.date}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Milestones + Work Breakdown */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Milestones */}
              <div className="lg:col-span-1 bg-white rounded-xl shadow p-6">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Milestones
                </h2>
                <div className="space-y-3">
                  {milestones.length === 0 ? (
                    <p className="text-xs text-gray-500">No milestones yet.</p>
                  ) : (
                    milestones.map((m, idx) => (
                      <Milestone
                        key={idx}
                        title={m.title}
                        description={m.description}
                        status={m.status}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Work Breakdown */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Work Breakdown
                  </h2>
                  <span className="text-xs text-gray-400">
                    Real-time status tracking
                  </span>
                </div>

                <div className="space-y-4">
                  {workItems.length === 0 ? (
                    <p className="text-xs text-gray-500">No work breakdown data available.</p>
                  ) : (
                    workItems.map((item) => (
                      <ProgressBar
                        key={item.label}
                        label={item.label}
                        value={item.value}
                        color={item.color}
                      />
                    ))
                  )}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

const ProgressBar: React.FC<WorkItem> = ({ label, value, color }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500">{value}%</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-2.5 rounded-full ${color}`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

const Milestone: React.FC<{
  title: string;
  description: string;
  status: MilestoneStatus;
}> = ({ title, description, status }) => {
  const statusConfig: Record<
    MilestoneStatus,
    { label: string; className: string }
  > = {
    completed: {
      label: "Completed",
      className: "bg-green-50 text-green-700",
    },
    "in-progress": {
      label: "In Progress",
      className: "bg-yellow-50 text-yellow-700",
    },
    pending: {
      label: "Pending",
      className: "bg-gray-50 text-gray-600",
    },
  };

  const cfg = statusConfig[status];

  return (
    <div className="border border-gray-100 rounded-lg p-4 hover:border-green-200 transition-colors bg-gray-50/60">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-gray-800">{title}</h3>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${cfg.className}`}
        >
          {cfg.label}
        </span>
      </div>
    </div>
  );
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default Dashboard;

